/* ----------------------------------------------------------------------------
   M1 · Add / Edit Courier Code drawer — wired to the real backend:
   create → POST courierPartner/create (cpAccountCode, name, prozoId, email,
   parentId, type, serviceTypePrimary/Secondary + ALL backend-mandatory
   attributes — weights / dims / cutoffs / SLA / tracking, collected in the
   compact "Courier attributes" section below + cpId/contactNo and the
   Configuration block: isExternal(+merchant)/qcType/isBlended/isSddNddEnabled);
   edit   → PATCH courierPartner {id, ...fieldsToPatch}.
   Select options (service types, weight rule, working days, NDR handling,
   tracking type) come from GET order/serviceability/intel/form-options with
   the static lists as fallback — same source as the legacy AddCourier form.
   Parent select shows ACTIVE parents only (backend 409s on DRAFT/INACTIVE).
   Backend validation errors are surfaced via apiError in the footer + toast.
   ---------------------------------------------------------------------------- */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckIcon from '@mui/icons-material/Check'
import SearchIcon from '@mui/icons-material/Search'
import {SideDrawer, SearchSelect, LogicalName} from '../components'
import {SERVICE_TYPES} from '../dummyData/serviceTypes' /* static label constants */
import {apiError} from '../supplyMeta'
import {pick} from './codeData'
import {apiConstants, get, postGeneric} from '../../../common/constants'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const CODE_RE = /^[A-Za-z0-9_]+$/
const PHONE_RE = /^[0-9]{10,15}$/
const INFO = {width: 13, height: 13}

/* Same lookup the legacy Courier Master form uses (AddCourier.js) — lists for
   service types / weight rule / working days / NDR handling / tracking type. */
const FORM_OPTIONS_URL = 'order/serviceability/intel/form-options'
/* ATTRS field id → form-options key (pickup/delivery days share workingDays) */
const FORM_OPTIONS_KEY = {
  chargeableWeightRule: 'chargeableWeightRule',
  pickupDays: 'workingDays',
  deliveryDays: 'workingDays',
  ndrHandling: 'ndrHandling',
  trackingType: 'trackingType',
}

/* normalize a form-options list to [{value,label}], dropping the '(none)'
   sentinel rows; returns null when the list is missing/empty (→ static fallback) */
const normOptions = (list) => {
  if (!Array.isArray(list)) return null
  const out = list
    .map((o) =>
      o && typeof o === 'object'
        ? {value: String(o.value == null ? '' : o.value), label: String(o.label == null ? o.value : o.label)}
        : {value: String(o), label: String(o)}
    )
    .filter((o) => o.value !== '' && o.label !== '(none)')
  return out.length ? out : null
}

/* ── backend-mandatory attribute fields (ProshipCourierPartner contract) ──── */
/* kind: num | sel | chk | text | time | date ; opt: not required to save     */
const ATTRS = [
  {id: 'minChargeableWeight', label: 'Min chargeable wt (kg)', kind: 'num'},
  {id: 'maxWeightSinglePiece', label: 'Max wt / piece (kg)', kind: 'num'},
  {id: 'maxWeightPerShipment', label: 'Max wt / shipment (kg)', kind: 'num'},
  {id: 'volumetricDivisor', label: 'Volumetric divisor', kind: 'num'},
  /* Allocation gate (like COD/Prepaid): max(dead, volumetric) kg must fall inside this band or the
     courier is skipped for the next one. Either side may be blank = open. */
  {id: 'effectiveMinWeight', label: 'Effective min wt (kg) — allocation', kind: 'num', opt: true},
  {id: 'effectiveMaxWeight', label: 'Effective max wt (kg) — allocation', kind: 'num', opt: true},
  {id: 'chargeableWeightRule', label: 'Chargeable wt rule', kind: 'sel', options: ['DW', 'VOL', 'HIGHER']},
  {id: 'maxLength', label: 'Max length (cm)', kind: 'num'},
  {id: 'maxWidth', label: 'Max width (cm)', kind: 'num'},
  {id: 'maxHeight', label: 'Max height (cm)', kind: 'num'},
  {id: 'maxGirth', label: 'Max girth (cm)', kind: 'num'},
  {id: 'maxLwhSum', label: 'Max L+W+H (cm)', kind: 'num'},
  {id: 'multiPieceAllowed', label: 'Multi-piece allowed', kind: 'chk'},
  {id: 'maxPiecesPerShipment', label: 'Max pieces / shipment', kind: 'num'},
  {id: 'fragileHandlingAvailable', label: 'Fragile handling', kind: 'chk'},
  {id: 'specialPackagingRequired', label: 'Special packaging', kind: 'chk'},
  {id: 'metroServiceable', label: 'Metro serviceable', kind: 'chk'},
  {id: 'nddEligible', label: 'NDD eligible', kind: 'chk'},
  {id: 'sddEligible', label: 'SDD eligible', kind: 'chk'},
  {id: 'forwardSlaMetroDays', label: 'Fwd SLA — metro (days)', kind: 'num'},
  {id: 'forwardSlaNonMetroDays', label: 'Fwd SLA — non-metro (days)', kind: 'num'},
  {id: 'pickupCutoffTime', label: 'Pickup cutoff (HH:mm)', kind: 'time'},
  {id: 'pickupDays', label: 'Pickup days', kind: 'sel', options: ['MON_SAT', 'MON_SUN']},
  {id: 'maxDeliveryAttempts', label: 'Max delivery attempts', kind: 'num'},
  {id: 'deliveryDays', label: 'Delivery days', kind: 'sel', options: ['MON_SAT', 'MON_SUN']},
  {id: 'ndrHandling', label: 'NDR handling', kind: 'sel', options: ['PROZO', 'COURIER', 'BOTH'], opt: true},
  {id: 'realTimeTracking', label: 'Real-time tracking', kind: 'chk'},
  {id: 'trackingType', label: 'Tracking type', kind: 'sel', options: ['API', 'WEB', 'BOTH']},
  {id: 'onboardedDate', label: 'Onboarded date', kind: 'date'},
  {id: 'dispatchMode', label: 'Dispatch mode', kind: 'sel', options: ['SURFACE', 'AIR']},
  {id: 'supportedShipmentType', label: 'Shipment type', kind: 'sel', options: ['B2C', 'B2B']},
  {id: 'integration', label: 'Integration', kind: 'sel', options: ['CP', 'NONAPI', 'ESZ']},
  {id: 'isCod', label: 'COD supported', kind: 'chk'},
  {id: 'isPrepaid', label: 'Prepaid supported', kind: 'chk'},
  {id: 'remarks', label: 'Remarks', kind: 'text'},
  /* Courier Code Display (phase 1): rank among account codes sharing a logical name. 1 = picked first;
     blank = unset (sorts last). Positive integer — the backend rejects 0 or negatives. */
  {id: 'priority', label: 'Priority (1 = picked first)', kind: 'num', opt: true},
]

/* Payment-mode capability flags: settable on create, frozen on edit (the PATCH API ignores them). The only
   way to change them afterwards is Couriers → Bulk attribute upload, which is audited. */
const FROZEN_ON_EDIT = new Set(['isCod', 'isPrepaid'])

/* Attributes start EMPTY by design — they are facts from the courier's rate card /
   MSA and must be entered consciously. Only onboardedDate (today, editable) is
   prefilled; checkboxes start unticked. */
const attrDefaults = () => ({
  minChargeableWeight: '',
  maxWeightSinglePiece: '',
  maxWeightPerShipment: '',
  volumetricDivisor: '',
  effectiveMinWeight: '',
  effectiveMaxWeight: '',
  chargeableWeightRule: '',
  maxLength: '',
  maxWidth: '',
  maxHeight: '',
  maxGirth: '',
  maxLwhSum: '',
  multiPieceAllowed: false,
  maxPiecesPerShipment: '',
  fragileHandlingAvailable: false,
  specialPackagingRequired: false,
  metroServiceable: false,
  nddEligible: false,
  sddEligible: false,
  forwardSlaMetroDays: '',
  forwardSlaNonMetroDays: '',
  pickupCutoffTime: '',
  pickupDays: '',
  maxDeliveryAttempts: '',
  deliveryDays: '',
  ndrHandling: '',
  realTimeTracking: false,
  trackingType: '',
  onboardedDate: new Date().toISOString().slice(0, 10),
  dispatchMode: '',
  supportedShipmentType: 'B2C',
  integration: '',
  isCod: false,
  isPrepaid: false,
  remarks: '',
  priority: '',
})

const emptyForm = () => ({
  parentId: '',
  code: '',
  display: '',
  prozo: '',
  email: '',
  cpId: '',
  contactNo: '',
  isExternal: 'false' /* default No — the only pre-set value among the new fields */,
  merchantId: '',
  qcType: '',
  isBlended: '',
  isSddNddEnabled: '',
  type: 'FORWARD',
  serviceTypePrimary: '',
  serviceTypeSecondary: '',
  ...attrDefaults(),
})

/* tri-state boolean → 'true' | 'false' | '' (for the optional Yes/No selects) */
const boolStr = (v) =>
  v === true || v === 'true' ? 'true' : v === false || v === 'false' ? 'false' : ''

/* prefill from the normalized row + raw backend doc (camel OR snake keys) */
const formFrom = (c) => {
  const raw = c.raw || {}
  const F = emptyForm()
  F.parentId = c.parentId || ''
  F.code = c.code || ''
  F.display = c.display || ''
  F.prozo = c.prozoId || ''
  F.email = c.email || ''
  F.type = pick(raw, 'type') || 'FORWARD'
  const cpId = pick(raw, 'cpId', 'cp_id')
  F.cpId = cpId == null ? '' : String(cpId)
  const contactNo = pick(raw, 'contactNo', 'contact_no')
  F.contactNo = contactNo == null ? '' : String(contactNo)
  F.isExternal = boolStr(pick(raw, 'isExternal', 'is_external')) === 'true' ? 'true' : 'false'
  const mid = pick(raw, 'merchantId', 'merchant_id', 'merchant')
  F.merchantId = mid == null ? '' : typeof mid === 'object' ? String(pick(mid, 'id', '_id', '$id') || '') : String(mid)
  F.qcType = pick(raw, 'qcType', 'qc_type') || ''
  F.isBlended = boolStr(pick(raw, 'isBlended', 'is_blended'))
  F.isSddNddEnabled = boolStr(pick(raw, 'isSddNddEnabled', 'is_sdd_ndd_enabled'))
  F.serviceTypePrimary = pick(raw, 'serviceTypePrimary', 'service_type_primary') || ''
  F.serviceTypeSecondary = pick(raw, 'serviceTypeSecondary', 'service_type_secondary') || ''
  ATTRS.forEach((a) => {
    const snake = a.id.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase())
    const v = pick(raw, a.id, snake)
    if (v == null) return
    if (a.kind === 'chk') F[a.id] = v === true || v === 'true' || v === 1 || v === '1'
    else F[a.id] = String(v)
  })
  return F
}

/* field wrapper — .field/.invalid markup so the scoped CSS shows help vs vmsg */
function Field({label, opt, help, msg, err, children}) {
  return (
    <div className={'field' + (err ? ' invalid' : '')}>
      <label>
        {label} {opt ? <span className='opt'>(optional)</span> : <span className='req'>*</span>}
      </label>
      {children}
      {help ? (
        <div className='help'>
          <InfoOutlinedIcon style={INFO} />
          {help}
        </div>
      ) : null}
      <div className='vmsg'>{typeof err === 'string' ? err : msg}</div>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string,
  opt: PropTypes.bool,
  help: PropTypes.string,
  msg: PropTypes.string,
  err: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  children: PropTypes.node,
}

/* single-value searchable select (merchant picker) — reuses the scoped
   .searchms/.ms-* styles from SearchMultiSelect, but picks exactly one value */
function MerchantSearchSelect({options, value, onChange, placeholder, loading}) {
  const [q, setQ] = useState('')
  const [openList, setOpenList] = useState(false)
  const selected = (options || []).find((o) => o.value === value)
  const qq = q.trim().toLowerCase()
  const matches = (options || []).filter((o) => !qq || o.label.toLowerCase().indexOf(qq) >= 0)
  const cap = matches.slice(0, 50)
  return (
    <div className='searchms' style={{display: 'block'}}>
      <div className='ms-search'>
        <span className='ic'>
          <SearchIcon style={{width: 15, height: 15}} />
        </span>
        <input
          className='input'
          type='text'
          autoComplete='off'
          placeholder={loading ? 'Loading merchants…' : placeholder}
          value={openList ? q : selected ? selected.label : ''}
          onFocus={() => {
            setQ('')
            setOpenList(true)
          }}
          onBlur={() => setTimeout(() => setOpenList(false), 150)}
          onChange={(e) => {
            setQ(e.target.value)
            setOpenList(true)
          }}
        />
        {openList ? (
          <div className='ms-results'>
            {cap.length ? (
              <>
                {cap.map((o) => (
                  <div
                    key={o.value}
                    className='ms-opt'
                    onMouseDown={(e) => {
                      e.preventDefault()
                      onChange(o.value)
                      setOpenList(false)
                    }}
                  >
                    {o.label}
                  </div>
                ))}
                {matches.length > cap.length ? (
                  <div className='ms-empty'>+{matches.length - cap.length} more — keep typing to narrow.</div>
                ) : null}
              </>
            ) : (
              <div className='ms-empty'>{loading ? 'Loading…' : 'No matching merchant.'}</div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

MerchantSearchSelect.propTypes = {
  options: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
}

export default function CodeFormDrawer({open, existing, codes, parents, onClose, onCommit, toast}) {
  const isEdit = !!existing
  const [F, setF] = useState(emptyForm)
  const [errs, setErrs] = useState({})
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState(null)

  /* dropdown lookups from the real API (same endpoint as legacy AddCourier);
     null → fall back to the static lists baked into ATTRS / SERVICE_TYPES */
  const [formOpts, setFormOpts] = useState(null)
  const formOptsTried = useRef(false)

  /* merchant list for the external-courier picker — fetched lazily, once */
  const [merchants, setMerchants] = useState(null)
  const [merchantsLoading, setMerchantsLoading] = useState(false)
  const merchantsTried = useRef(false)

  useEffect(() => {
    if (open) {
      setF(existing ? formFrom(existing) : emptyForm())
      setErrs({})
      setSaving(false)
      setServerError(null)
    }
  }, [open, existing])

  /* lazy, once — same source the legacy Courier Master form uses */
  useEffect(() => {
    if (!open || formOptsTried.current) return
    formOptsTried.current = true
    get(FORM_OPTIONS_URL)
      .then((res) => {
        const opts = (res && res.data && res.data.responseObj) || (res && res.data) || null
        if (opts && typeof opts === 'object') setFormOpts(opts)
      })
      .catch(() => {
        /* static fallback lists stay in place */
      })
  }, [open])

  /* merchant list — same API + filter + option mapping as MerchantFilterCached
     (merchant/searchCached, KYC_APPROVED non-test merchants) */
  useEffect(() => {
    if (!open || F.isExternal !== 'true' || merchantsTried.current) return
    merchantsTried.current = true
    setMerchantsLoading(true)
    postGeneric(apiConstants.MERCHANT_SEARCH_CACHED, {
      andfilter: [
        {field: 'isTestMerchant', operator: 'ne', value: true},
        {field: 'status', operator: 'eq', value: 'KYC_APPROVED'},
      ],
      offset: 0,
      limit: 2000,
      includes: 'id,name,merchantId,is_deleted,isPTLMerchant,isD2CMerchant,companyName',
    })
      .then((res) => {
        const body = res && res.data
        const list = Array.isArray(body) ? body : (body && (body.data || body.responseObj)) || []
        setMerchants(
          (Array.isArray(list) ? list : [])
            .filter((m) => m.is_deleted === '0')
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
            .map((m) => ({value: m.id, label: m.companyName ? m.name + ' (' + m.companyName + ')' : m.name}))
        )
      })
      .catch(() => setMerchants([]))
      .finally(() => setMerchantsLoading(false))
  }, [open, F.isExternal])

  /* parent must be ACTIVE to anchor a new code (backend 409s otherwise) */
  const activeParents = useMemo(
    () => (parents || []).filter((p) => String(pick(p, 'status') || '').toUpperCase() === 'ACTIVE'),
    [parents]
  )

  const clearErr = (id) => setErrs((prev) => (prev[id] ? {...prev, [id]: false} : prev))
  const set = (id, val) => {
    setF((prev) => ({...prev, [id]: val}))
    clearErr(id)
  }

  const onCode = (val) => {
    setF((prev) => {
      const next = {...prev, code: val}
      /* auto-suggest Prozo ID as [code]_PRZ_1 while it is untouched */
      if (!prev.prozo || /_PRZ_1$/.test(prev.prozo)) next.prozo = val ? val + '_PRZ_1' : ''
      return next
    })
    /* TRD M1.3-02 — real-time uniqueness as you type (case-sensitive cpAccountCode) */
    const dup =
      val && (codes || []).some((c) => c.code === val.trim() && (!existing || c.code !== existing.code))
    setErrs((prev) => ({...prev, code: dup ? 'Courier code "' + val.trim() + '" already exists.' : false}))
  }

  const save = () => {
    if (saving) return
    const bad = {}
    if (!F.parentId) bad.parent = true
    if (!CODE_RE.test((F.code || '').trim())) bad.code = true
    if ((F.display || '').trim().length < 3) bad.display = true
    if (!(F.prozo || '').trim()) bad.prozo = true
    if (!EMAIL_RE.test((F.email || '').trim())) bad.email = true
    if (String(F.cpId).trim() === '' || isNaN(Number(F.cpId))) bad.cpId = true
    if (!PHONE_RE.test((F.contactNo || '').trim())) bad.contactNo = true
    if (F.isExternal === 'true' && !F.merchantId) bad.merchantId = true
    if (!F.type) bad.type = true
    if (!F.serviceTypePrimary) bad.serviceTypePrimary = true

    /* uniqueness (cpAccountCode + Prozo ID), excluding the row being edited */
    if (!bad.code) {
      const dupCode = (codes || []).some(
        (c) => (!existing || c.code !== existing.code) && c.code === F.code.trim()
      )
      if (dupCode) bad.code = 'Courier code "' + F.code.trim() + '" already exists.'
    }
    const dupProzo = (codes || []).some(
      (c) => (!existing || c.code !== existing.code) && c.prozoId === F.prozo.trim()
    )
    if (dupProzo) bad.prozo = true

    /* backend-mandatory attributes */
    ATTRS.forEach((a) => {
      if (a.opt || a.kind === 'chk') return
      const v = F[a.id]
      if (v == null || String(v).trim() === '') bad[a.id] = true
      else if (a.kind === 'num' && isNaN(Number(v))) bad[a.id] = true
    })

    if (Object.keys(bad).some((k) => bad[k])) {
      setErrs(bad)
      toast('Please fix the highlighted fields.', 'error')
      return
    }

    /* ── build the backend doc ── */
    const doc = {
      cpAccountCode: F.code.trim(),
      name: F.display.trim(),
      prozoId: F.prozo.trim(),
      email: F.email.trim(),
      type: F.type,
      cpId: Number(F.cpId),
      contactNo: F.contactNo.trim(),
      serviceTypePrimary: F.serviceTypePrimary,
    }
    /* external (merchant-owned) courier → merchantId travels with isExternal:true;
       otherwise isExternal:false and NO merchantId */
    if (F.isExternal === 'true') {
      doc.isExternal = true
      doc.merchantId = F.merchantId
    } else {
      doc.isExternal = false
    }
    if (F.qcType) doc.qcType = F.qcType
    if (F.isBlended) doc.isBlended = F.isBlended === 'true'
    if (F.isSddNddEnabled) doc.isSddNddEnabled = F.isSddNddEnabled === 'true'
    if (F.serviceTypeSecondary) doc.serviceTypeSecondary = F.serviceTypeSecondary
    doc.parentId = F.parentId
    ATTRS.forEach((a) => {
      /* isCod / isPrepaid are frozen on PATCH (backend ignores them) — do not send them on edit so the
         request reflects what will actually change. They are still sent on create. */
      if (existing && FROZEN_ON_EDIT.has(a.id)) return
      /* the model exposes this one via @JsonProperty("supported_shipment") — camelCase is ignored */
      const key = a.id === 'supportedShipmentType' ? 'supported_shipment' : a.id
      const v = F[a.id]
      if (a.kind === 'chk') doc[key] = !!v
      /* optional numbers left blank are OMITTED — Number('') is 0, and e.g. effectiveMaxWeight: 0 would
         block every order on the courier. Mandatory numbers are non-blank here (validated above). */
      else if (a.kind === 'num') { if (v == null || String(v).trim() === '') return; doc[key] = Number(v) }
      else if (v != null && String(v).trim() !== '') doc[key] = String(v).trim()
      /* optional empty selects/text (e.g. ndrHandling, remarks) are omitted */
    })

    setSaving(true)
    setServerError(null)
    Promise.resolve(onCommit({existing, doc})).catch((e) => {
      const msg = apiError(e)
      setServerError(msg)
      toast(msg, 'error')
      setSaving(false)
    })
  }

  /* service types: API list when available, static SERVICE_TYPES as fallback */
  const serviceTypeOptions = useMemo(
    () =>
      normOptions(formOpts && formOpts.serviceTypes) ||
      SERVICE_TYPES.map((s) => ({value: s.code, label: s.code + ' — ' + s.name})),
    [formOpts]
  )
  /* select options for an ATTRS field: form-options list when fetched,
     otherwise the static list baked into the field definition */
  const attrOptions = (a) =>
    normOptions(formOpts && formOpts[FORM_OPTIONS_KEY[a.id]]) ||
    (a.options || []).map((o) => ({value: o, label: o}))

  const renderAttr = (a) => {
    if (a.kind === 'chk') {
      const frozen = !!existing && FROZEN_ON_EDIT.has(a.id)
      return (
        <label
          key={a.id}
          className='checkrow'
          style={{padding: '6px 6px', alignSelf: 'end', opacity: frozen ? 0.6 : 1, cursor: frozen ? 'not-allowed' : undefined}}
          title={frozen ? 'Frozen after create. Change via Couriers → Bulk attribute upload (audited), then check Courier Performance → Payment Mode Compliance.' : undefined}
        >
          <input type='checkbox' checked={!!F[a.id]} disabled={frozen} onChange={(e) => set(a.id, e.target.checked)} />
          <span style={{fontSize: 12.5}}>
            {a.label}
            {frozen ? <span className='muted' style={{marginLeft: 6, fontSize: 11}}>(bulk upload only)</span> : null}
          </span>
        </label>
      )
    }
    return (
      <Field key={a.id} label={a.label} opt={!!a.opt} msg={a.kind === 'num' ? 'Enter a number.' : 'Required.'} err={errs[a.id]}>
        {a.kind === 'sel' ? (
          <SearchSelect
            value={F[a.id]}
            onChange={(v) => set(a.id, v)}
            options={attrOptions(a)}
            placeholder={a.opt ? '—' : 'Search…'}
          />
        ) : (
          <input
            className='input'
            type={a.kind === 'num' ? 'number' : a.kind === 'time' ? 'time' : a.kind === 'date' ? 'date' : 'text'}
            value={F[a.id]}
            onChange={(e) => set(a.id, e.target.value)}
          />
        )}
      </Field>
    )
  }

  return (
    <SideDrawer
      open={!!open}
      onClose={onClose}
      title={isEdit ? 'Edit Courier Code' : 'Add Courier Code'}
      width={640}
      footer={
        <>
          {serverError ? (
            <span style={{color: 'var(--error, #C62828)', fontSize: 12, marginRight: 'auto', maxWidth: 360}}>
              {serverError}
            </span>
          ) : null}
          <button className='btn btn-ghost' onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className='btn btn-primary' onClick={save} disabled={saving}>
            <CheckIcon style={{width: 16, height: 16}} />
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Code'}
          </button>
        </>
      }
    >
      <Field
        label='Parent courier'
        help='Active parents only — the backend rejects codes on DRAFT/INACTIVE parents. Onboard new parents via Partner Directory.'
        msg='Select a parent courier.'
        err={errs.parent}
      >
        <SearchSelect
          value={F.parentId}
          onChange={(v) => set('parentId', v) || clearErr('parent')}
          placeholder='Select parent courier…'
          options={(isEdit ? parents || [] : activeParents).map((p) => {
            const pid = pick(p, 'id', '_id')
            return {value: pid, label: pick(p, 'name') || pid}
          })}
        />
      </Field>

      <Field
        label='Courier code (cpAccountCode)'
        help='Exact account code used in ProShip and the courier API. Unique, case-sensitive, no spaces (alphanumeric + underscores).'
        msg='Code must be unique — alphanumeric and underscores only.'
        err={errs.code}
      >
        <input
          className='input mono'
          value={F.code}
          placeholder='Delhivery_ENT_SURFACE'
          onChange={(e) => onCode(e.target.value)}
        />
      </Field>

      <Field
        label='Display name'
        help='Human-readable name shown in reports. Minimum 3 characters.'
        msg='Enter a display name of at least 3 characters.'
        err={errs.display}
      >
        <input
          className='input'
          value={F.display}
          placeholder='Delhivery ENT Surface'
          onChange={(e) => set('display', e.target.value)}
        />
      </Field>

      <Field
        label='Prozo Courier ID'
        help='Internal Prozo ID. Auto-suggested as [code]_PRZ_1 but editable. Must be unique.'
        msg='Prozo ID is required and must be unique.'
        err={errs.prozo}
      >
        <input
          className='input mono'
          value={F.prozo}
          placeholder='Delhivery_ENT_SURFACE_PRZ_1'
          onChange={(e) => set('prozo', e.target.value)}
        />
      </Field>

      <Field label='CP Id (Clickpost)' help='Clickpost courier id' msg='Enter the numeric Clickpost courier id.' err={errs.cpId}>
        <input
          className='input mono'
          type='number'
          value={F.cpId}
          placeholder='123'
          onChange={(e) => set('cpId', e.target.value)}
        />
      </Field>

      <Field label='Direction (type)' msg='Select FORWARD or RVP.' err={errs.type}>
        <SearchSelect
          value={F.type}
          onChange={(v) => set('type', v)}
          options={[
            {value: 'FORWARD', label: 'FORWARD'},
            {value: 'RVP', label: 'RVP (reverse)'},
          ]}
        />
      </Field>

      <Field
        label='Primary service type'
        help='PACE uses this to match orders to the code.'
        msg='Select the primary service type.'
        err={errs.serviceTypePrimary}
      >
        <SearchSelect
          value={F.serviceTypePrimary}
          onChange={(v) => set('serviceTypePrimary', v)}
          options={serviceTypeOptions}
          placeholder='Select service type…'
        />
      </Field>

      <Field label='Secondary service type' opt msg='' err={false}>
        <SearchSelect
          value={F.serviceTypeSecondary}
          onChange={(v) => set('serviceTypeSecondary', v)}
          options={serviceTypeOptions}
          placeholder='—'
        />
      </Field>

      {/* Courier Code Display: derived on the backend from parent + primary service type + min chargeable
          weight. Read-only on purpose — the form never replicates the naming rule. */}
      <Field
        label='Logical name'
        opt
        help={
          isEdit
            ? 'Generated by the naming rule from this code’s attributes. Updates automatically when any of them change.'
            : 'Generated on save by the naming rule from this code’s attributes.'
        }
        msg=''
        err={false}
      >
        {isEdit && existing && existing.logicalName ? (
          <div style={{padding: '6px 0'}}>
            <LogicalName value={existing.logicalName} priority={existing.priority} size='lg' />
          </div>
        ) : (
          <input className='input' value='' placeholder={isEdit ? 'Not named yet' : 'Appears after saving'} disabled readOnly />
        )}
      </Field>

      <Field
        label='Operational email'
        help='Courier operational inbox for this code. Used for pickup requests and exception communications.'
        msg='Enter a valid email address.'
        err={errs.email}
      >
        <input
          className='input'
          value={F.email}
          placeholder='ops@courier.com'
          onChange={(e) => set('email', e.target.value)}
        />
      </Field>

      <Field
        label='Contact number'
        help='Courier operational contact — 10 to 15 digits.'
        msg='Enter a contact number of 10–15 digits.'
        err={errs.contactNo}
      >
        <input
          className='input'
          value={F.contactNo}
          placeholder='9876543210'
          onChange={(e) => set('contactNo', e.target.value)}
        />
      </Field>

      {/* ── configuration (legacy Courier Master parity) ── */}
      <div className='seclabel' style={{margin: '14px 0 4px'}}>
        Configuration
      </div>

      <Field
        label='External (merchant-owned)'
        opt
        help='Yes = this code belongs to a specific merchant (their own courier account).'
        msg=''
        err={false}
      >
        <SearchSelect
          value={F.isExternal}
          onChange={(v) => {
            setF((prev) => ({...prev, isExternal: v, merchantId: v === 'true' ? prev.merchantId : ''}))
            clearErr('isExternal')
            clearErr('merchantId')
          }}
          options={[
            {value: 'false', label: 'No'},
            {value: 'true', label: 'Yes'},
          ]}
        />
      </Field>

      {F.isExternal === 'true' ? (
        <Field
          label='Merchant'
          help='The merchant who owns this courier account. Required for external codes.'
          msg='Select the owning merchant.'
          err={errs.merchantId}
        >
          <MerchantSearchSelect
            options={merchants || []}
            value={F.merchantId}
            onChange={(v) => set('merchantId', v)}
            placeholder='Type to search merchants…'
            loading={merchantsLoading || merchants == null}
          />
        </Field>
      ) : null}

      <Field label='QC type' opt msg='' err={false}>
        <SearchSelect
          value={F.qcType}
          onChange={(v) => set('qcType', v)}
          placeholder='—'
          options={[
            {value: 'doorstep', label: 'Yes (doorstep)'},
            {value: 'No', label: 'No'},
          ]}
        />
      </Field>

      <Field label='Blended' opt msg='' err={false}>
        <SearchSelect
          value={F.isBlended}
          onChange={(v) => set('isBlended', v)}
          placeholder='—'
          options={[
            {value: 'true', label: 'Yes'},
            {value: 'false', label: 'No'},
          ]}
        />
      </Field>

      <Field label='SDD / NDD enabled' opt msg='' err={false}>
        <SearchSelect
          value={F.isSddNddEnabled}
          onChange={(v) => set('isSddNddEnabled', v)}
          placeholder='—'
          options={[
            {value: 'true', label: 'Yes'},
            {value: 'false', label: 'No'},
          ]}
        />
      </Field>

      {/* ── backend-mandatory courier attributes ── */}
      <div className='seclabel' style={{margin: '14px 0 4px'}}>
        Courier attributes
      </div>
      <div className='help' style={{marginBottom: 8}}>
        <InfoOutlinedIcon style={INFO} />
        These weight / dimension / cutoff / tracking attributes are mandatory on the backend create API.
      </div>
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14}}>
        {ATTRS.map(renderAttr)}
      </div>
    </SideDrawer>
  )
}

CodeFormDrawer.propTypes = {
  open: PropTypes.bool,
  existing: PropTypes.object,
  codes: PropTypes.array.isRequired,
  parents: PropTypes.array,
  onClose: PropTypes.func,
  onCommit: PropTypes.func.isRequired,
  toast: PropTypes.func.isRequired,
}
